import {
  Component,
  input,
  signal,
  OnInit,
  effect,
  ViewChild,
  ElementRef,
  computed,
  Signal,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSelect } from '@angular/material/select';
import { Logger, LogService } from '@/app/core';
import { SelectorOption } from '@/app/modules/saved-views/types';
import { DeserializeSavedViewNamePipe } from '@/app/modules/saved-views/pipes/deserialize-saved-view-name.pipe';

@Component({
  selector: 'rdo-saved-view-selector',
  template: `
    <mat-form-field>
      <mat-select
        #selectElement
        id="SavedViewSelect"
        data-testid="SavedViewSelect"
        class="full-width"
        [formControl]="formControl"
        [placeholder]="defaultLabel"
        panelClass="rdo-saved-view-selector-panel"
      >
        <mat-select-trigger>
          {{ (formControl.value | deserializeSavedViewName) || defaultLabel }}
        </mat-select-trigger>

        @for (option of this.optionsList(); track trackView($index, option)) {
          <mat-option [value]="option.value">
            {{ option.label }}
          </mat-option>
        }

        <div class="manage-footer" (click)="onManage()">
          <button type="button" class="manage-btn">
            {{ 'main.filters.profiles.list.manage' | translate }}
          </button>
        </div>
      </mat-select>
    </mat-form-field>
  `,
  styleUrls: ['./saved-view-selector.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    TranslatePipe,
    MatInputModule,
    ReactiveFormsModule,
    DeserializeSavedViewNamePipe,
  ],
})
export class SavedViewSelectorComponent {
  @ViewChild('selectElement') selectElement!: MatSelect;
  private isInitialized = signal(false);
  public gridName = input<string>();
  public formControl = new FormControl(null);
  public defaultLabel = '';
  public isDisabled = computed(() => {
    const avail = this.savedViewsService.currentGridViewsList();
    return avail.length === 0;
  });

  public optionsList: Signal<SelectorOption<string>[]> = computed(() => {
    return this.savedViewsService.currentGridViewsList().map((view) => {
      return {
        label: this.savedViewsService.deserializeViewName(view.name),
        value: view.name,
      };
    });
  });
  public logger: Logger;

  constructor(
    private translateService: TranslateService,
    private logService: LogService,
    public savedViewsService: SavedViewsService,
    private cdr: ChangeDetectorRef
  ) {
    this.logger = this.logService.getModuleLogger(
      'SavedViews',
      'SavedViewSelector',
      false
    );
    this.defaultLabel = this.translateService.instant(
      'main.saved_views.no_report_selected'
    );

    this.formControl.valueChanges.subscribe((value: string) => {
      this.logger.info('formControl.value updated:', value);
      this.savedViewsService
        .setSelectedView(this.gridName(), value)
        .then((viewName: string | null) => {
          this.logger.info('form changed to: ', viewName);
        })
        .catch(() => {
          this.setFormControlWithSelectedViewName(
            this.savedViewsService.getSelectedViewName()
          );
        });
    });

    // Subscribe to signal changes and trigger change detection
    effect(() => {
      this.isDisabled();
      this.cdr.markForCheck();
    });

    // Apply the default view if the saved-views is just mounted.  That should
    // mean there was a page change and the default view needs to be determined.
    // The default view selection was removed from the service, so that the
    // general router behavior could control in the initiation of each page.
    // I didn't want to have to put logic into each page though so it goes here.
    effect(() => {
      if (
        this.savedViewsService.currentGridName() === this.gridName() &&
        this.savedViewsService.isLoaded() &&
        !this.isInitialized()
      ) {
        this.isInitialized.set(true);
        const defaultView = this.savedViewsService.getDefaultView();
        this.savedViewsService.setSelectedView(
          this.gridName(),
          defaultView?.name || null
        );
      }
    });

    // Watch for changes to the selected view that are not reflect in the form control
    effect((onCleanup) => {
      const selectedView = this.savedViewsService.getSelectedView();
      if (selectedView) {
        if (this.formControl.value !== selectedView?.name) {
          this.logger.info(
            'Updating formControl value to match selectedView:',
            selectedView
          );
          this.formControl.setValue(selectedView.name, { emitEvent: false });
        }
      }

      onCleanup(() => {
        this.logger.info('Cleaning up SavedViewSelectorComponent effect');
      });
    });

    // Effect to enable/disable the form control based on computed disabled state
    effect(() => {
      if (this.isDisabled()) {
        this.formControl.disable({ emitEvent: false });
      } else {
        this.formControl.enable({ emitEvent: false });
      }
    });
  }

  public onManage() {
    this.savedViewsService.isManagementDialogOpen.set(true);
    // Close the dropdown
    if (this.selectElement) {
      this.selectElement.close();
    }
  }

  public trackView(index: number, option: { label: string; value: string }) {
    return option.value;
  }

  private setFormControlWithSelectedViewName(name: string): void {
    this.formControl.setValue(name, {
      emitEvent: false,
    });
  }
}
